/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import './CreateAccount.css';
import TermsAndConditions from '../components/TermsAndConditions.tsx';

// Input sanitization helper
const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .slice(0, 100); // Limit length
};

// Email validation helper
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Password strength checker
const isStrongPassword = (password: string): boolean => {
  return (
    password.length >= 8 &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password)
  );
};

function CreateAccount() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [artist, setArtist] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Rate limiting check
    if (attemptCount >= 3) {
      setError('Too many attempts. Please wait a few minutes and try again.');
      return;
    }

    // Sanitize all inputs
    const sanitizedFirstName = sanitizeInput(firstName);
    const sanitizedLastName = sanitizeInput(lastName);
    const sanitizedArtist = sanitizeInput(artist);
    const sanitizedEmail = email.trim().toLowerCase();

    // Validation
    if (!sanitizedFirstName || sanitizedFirstName.length < 2) {
      setError('First name must be at least 2 characters');
      return;
    }

    if (!sanitizedLastName || sanitizedLastName.length < 2) {
      setError('Last name must be at least 2 characters');
      return;
    }

    if (!sanitizedArtist || sanitizedArtist.length < 2) {
      setError('Artist, business or band name must be at least 2 characters');
      return;
    }

    if (!isValidEmail(sanitizedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!termsAccepted) {
      setError('You must accept the terms & conditions');
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (!isStrongPassword(password)) {
      setError('Password must be at least 8 characters with uppercase, lowercase, and numbers');
      return;
    }

    setLoading(true);
    setAttemptCount(prev => prev + 1);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        sanitizedEmail,
        password
      );
      const user = userCredential.user;

      // Create user document with sanitized data
      await setDoc(doc(db, 'users', user.uid), {
        UserId: user.uid,
        email: user.email,
        firstname: sanitizedFirstName,
        lastname: sanitizedLastName,
        artist: sanitizedArtist,
        collectionHandles: [],
        createdAt: new Date().toISOString(),
        isAdmin: false,
        emailVerified: false,
        termsAcceptedAt: new Date().toISOString() // Simplified - just timestamp
      });

      // Send verification email with hardcoded URL
      const verificationUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
      
      try {
        await sendEmailVerification(user, {
          url: `${verificationUrl}/?emailVerified=true`,
          handleCodeInApp: false
        });
      } catch (emailError) {
        console.error('Error sending verification email:', emailError);
        // Don't reveal email error to user
      }

      // Reset attempt count on success
      setAttemptCount(0);
      navigate('/verify-email');
      
    } catch (error: any) {
      console.error('Error creating account:', error);
      
      // Generic error messages to prevent account enumeration
      switch (error.code) {
        case 'auth/email-already-in-use':
        case 'auth/invalid-email':
        case 'auth/weak-password':
          setError('Unable to create account. Please check your information and try again.');
          break;
        case 'auth/operation-not-allowed':
          setError('Account creation is temporarily disabled. Please contact support.');
          break;
        case 'auth/network-request-failed':
          setError('Network error. Please check your connection and try again.');
          break;
        default:
          setError('An error occurred. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-account-container">
      <h2>Create Account</h2>
      <div className="scrollable-area">
        <form onSubmit={handleSubmit} className="create-account-form">
          <div className="form-group">
            <label htmlFor="firstName">First Name: *</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              placeholder="Enter your first name"
              disabled={loading}
              maxLength={100}
              autoComplete="given-name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name: *</label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              placeholder="Enter your last name"
              disabled={loading}
              maxLength={100}
              autoComplete="family-name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="artist">Artist, Business or Band Name: *</label>
            <input
              type="text"
              id="artist"
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              required
              placeholder="Enter your artist, business or band name"
              disabled={loading}
              maxLength={100}
              autoComplete="organization"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email: *</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              disabled={loading}
              maxLength={254}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password: *</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="At least 8 characters with uppercase, lowercase, and numbers"
              disabled={loading}
              maxLength={128}
              autoComplete="new-password"
            />
            <span className="label-sub">At least 8 characters with uppercase, lowercase, and numbers</span>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password: *</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Re-enter your password"
              disabled={loading}
              maxLength={128}
              autoComplete="new-password"
            />
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                required
                disabled={loading}
              />
              <span>
                I accept the{' '}
                <button
                  type="button"
                  className="terms-link"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowTermsModal(true);
                  }}
                  disabled={loading}
                >
                  Merch Sherpa terms & conditions
                </button>
                {' '}*
              </span>
            </label>
          </div>

          {error && <div className="error-message" role="alert">{error}</div>}

          <button type="submit" disabled={loading || attemptCount >= 3} className="submit-button">
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
      </div>

      <div className="login-link">
        Already have an account? <a href="/">Log in</a>
      </div>

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Terms & Conditions</h3>
              <button
                className="modal-close"
                onClick={() => setShowTermsModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <TermsAndConditions />
            </div>
            <div className="modal-footer">
              <button
                className="modal-close-button"
                onClick={() => setShowTermsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateAccount;